import { useEffect, useRef, useState } from 'react'
import { FormProvider, useFieldArray, useForm, type FieldErrors } from 'react-hook-form'
import { ArrowUUpLeftIcon, PlusCircleIcon, TrashSimpleIcon } from '@phosphor-icons/react'
import { Box, useMediaQuery } from '@mui/material'

import { BaseModal2 } from '@components/modals/BaseModal2'
import { emotionTheme } from '@styles/theme'
import { initialFormValues } from './form/initialFormValues'
import { Input } from '@components/shared/Input'
import { ReturnModal } from '@components/modals/ReturnModal'
import { RowBox } from '@components/shared/RowBox'
import { Select } from '@components/shared/Select'
import { WarningIcon } from '@assets/svgs/WarningIcon'
import { usePreview } from '@pages/Preview/context'

import type { ReturnPreviousSelectedModalProps } from './types'
import type {
  BulkReturnPreviewAccountRowProps,
  BulkReturnPreviewCheckResultProps
} from '@pages/Preview/hooks/usePreview/types'

import * as S from './styles'

const buildAccountsFromCheckResults = (
  results: BulkReturnPreviewCheckResultProps[]
): BulkReturnPreviewAccountRowProps[] | null => {
  const reference = results.find((result) => result.returnData?.hasDraft && result.returnData.items?.length)
  if (!reference?.returnData) return null

  return reference.returnData.items.map((referenceItem) => {
    const draftIds: Record<string, string> = {}

    results.forEach((result) => {
      const match = result.returnData?.items?.find((item) => item.accountId === referenceItem.accountId)
      if (match) draftIds[result.itemKey] = String(match.id)
    })

    return {
      account: String(referenceItem.accountId),
      returnJustification: referenceItem.justification || '',
      draftIds
    }
  })
}

export const ReturnPreviousSelectedModal = ({
  open,
  onClose,
  accountOptions = [],
  returnHierarchy = 'hierarquia anterior',
  isCheckingDraftsLoading = false,
  isSavingDraftLoading = false,
  isSubmittingLoading = false,
  onCheckDrafts,
  onSaveDraft,
  onSubmitBulk,
  ...rest
}: ReturnPreviousSelectedModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)
  const { deleteDraftReturnPreview } = usePreview()

  const methods = useForm<any>({
    defaultValues: initialFormValues
  })

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'accounts'
  })

  const [openCancelDetailedSummaryModal, setOpenCancelDetailedSummaryModal] = useState(false)
  const [openReturnSelectedModal, setOpenReturnSelectedModal] = useState(false)
  const [hasDraftSaved, setHasDraftSaved] = useState(false)
  const [hasUnsavedDraftChanges, setHasUnsavedDraftChanges] = useState(false)
  const initialDraftSnapshotRef = useRef<Map<number, { account: string; returnJustification: string }>>(new Map())

  const handleAddAccount = () => {
    append({ account: '', returnJustification: '', draftIds: {} })
    if (hasDraftSaved) setHasUnsavedDraftChanges(true)
  }

  const handleRemoveAccount = async (index: number) => {
    const draftIds = methods.getValues(`accounts.${index}.draftIds`) as Record<string, string> | undefined
    const idsToDelete = draftIds ? Object.values(draftIds).filter(Boolean) : []

    if (idsToDelete.length) {
      await deleteDraftReturnPreview(idsToDelete)
    }

    remove(index)
  }

  const onError = (errors: FieldErrors<any>) => console.log({ errors })

  const handleSaveDraft = async (formData: any) => {
    if (!onSaveDraft) return

    const result = await onSaveDraft(formData.accounts)

    if (result) {
      methods.reset({ ...initialFormValues, accounts: result })
      setHasDraftSaved(true)
      setHasUnsavedDraftChanges(false)
      initialDraftSnapshotRef.current = new Map(
        result.map((acc, index) => [index, { account: acc.account, returnJustification: acc.returnJustification }])
      )
    }
  }

  const handleSubmitBulk = async () => {
    if (!onSubmitBulk) return

    const success = await onSubmitBulk()

    if (success) {
      setOpenReturnSelectedModal(false)
      onClose(false)
    }
  }

  const handleCloseModal = () => {
    if (hasDraftSaved && !hasUnsavedDraftChanges) {
      onClose(false)
    } else {
      setOpenCancelDetailedSummaryModal(true)
    }
  }

  useEffect(() => {
    if (!open) return

    onCheckDrafts().then((results) => {
      const accounts = buildAccountsFromCheckResults(results)

      if (accounts?.length) {
        methods.reset({ ...initialFormValues, accounts })
        setHasDraftSaved(true)
        setHasUnsavedDraftChanges(false)
        initialDraftSnapshotRef.current = new Map(
          accounts.map((acc, index) => [index, { account: acc.account, returnJustification: acc.returnJustification }])
        )
      } else {
        methods.reset(initialFormValues)
        setHasDraftSaved(false)
        setHasUnsavedDraftChanges(false)
        initialDraftSnapshotRef.current = new Map()
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) {
      methods.reset(initialFormValues)
      setHasDraftSaved(false)
      setHasUnsavedDraftChanges(false)
      initialDraftSnapshotRef.current = new Map()
    }
  }, [open])

  useEffect(() => {
    const subscription = methods.watch((values) => {
      if (!hasDraftSaved) return

      const accounts = values?.accounts
      if (!Array.isArray(accounts)) return

      const hasChange =
        accounts.length !== initialDraftSnapshotRef.current.size ||
        accounts.some((acc: { account?: string; returnJustification?: string }, index: number) => {
          const initial = initialDraftSnapshotRef.current.get(index)
          if (!initial) return true
          return acc?.account !== initial.account || (acc?.returnJustification ?? '') !== initial.returnJustification
        })

      setHasUnsavedDraftChanges(hasChange)
    })

    return () => subscription.unsubscribe()
  }, [methods, hasDraftSaved])

  const selectOptions = accountOptions
    .filter((opt) => opt.code?.toLowerCase() !== 'all' && opt.name?.toLowerCase() !== 'todos')
    .map((opt) => ({
      value: opt.code,
      label: opt.name
    }))

  const isReadyToSend = hasDraftSaved && !hasUnsavedDraftChanges

  return (
    <>
      <BaseModal2
        open={open}
        title="Devolver Selecionadas"
        headerIcon={<ArrowUUpLeftIcon />}
        onClose={handleCloseModal}
        width={isMobile ? 'calc(100% - 20px)' : 870}
        footer={{
          cancelButtonText: isReadyToSend ? 'Fechar' : 'Cancelar',
          cancelButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          confirmButtonText: isReadyToSend ? 'Enviar Devolução' : 'Salvar',
          confirmButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          onConfirm: () => {
            if (isReadyToSend) {
              setOpenReturnSelectedModal(true)
            } else {
              methods.handleSubmit(handleSaveDraft, onError)()
            }
          },
          loading: isCheckingDraftsLoading || isSavingDraftLoading
        }}
        {...rest}
      >
        <S.Title>Selecione a(s) Conta(s) e preencha o campo "Justificativa da devolução" antes de salvar.</S.Title>

        <S.Warning>
          Atenção: A conta e o motivo informados nesta devolução serão aplicados a todas as prévias selecionadas.
        </S.Warning>

        <FormProvider {...methods}>
          <S.Form>
            {fields.map((field, index) => (
              <S.InputsBox key={field.id}>
                <input type="hidden" {...methods.register(`accounts.${index}.draftIds`)} />
                <RowBox columns={3}>
                  <Select
                    required
                    name={`accounts.${index}.account`}
                    label="Conta"
                    placeholder="Selecione a conta"
                    options={selectOptions}
                  />
                  <Box sx={{ gridColumn: 'span 2' }}>
                    <Input required name={`accounts.${index}.returnJustification`} label="Justificativa da devolução" />
                  </Box>
                </RowBox>

                <S.AddAccountButton onClick={handleAddAccount}>
                  <PlusCircleIcon />
                </S.AddAccountButton>

                {fields.length > 1 && (
                  <S.AddAccountButton onClick={() => handleRemoveAccount(index)}>
                    <TrashSimpleIcon />
                  </S.AddAccountButton>
                )}
              </S.InputsBox>
            ))}
          </S.Form>
        </FormProvider>
      </BaseModal2>

      <ReturnModal
        open={openCancelDetailedSummaryModal}
        headerIcon={<WarningIcon />}
        title="Deseja realmente cancelar a ação?"
        message="Todo o seu progresso será perdido!"
        headerTitle="Você tem certeza?"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenCancelDetailedSummaryModal}
        onConfirm={() => {
          onClose(false)
          setOpenCancelDetailedSummaryModal(false)
        }}
      />

      <ReturnModal
        open={openReturnSelectedModal}
        headerIcon={<ArrowUUpLeftIcon size={24} weight="bold" />}
        headerTitle="Devolver Selecionadas"
        message={`Tem certeza de que deseja devolver as Prévias selecionadas para ajuste do(a) ${returnHierarchy}?`}
        warningMessage="Após confirmação, esta ação não poderá ser desfeita."
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenReturnSelectedModal}
        isLoading={isSubmittingLoading}
        onConfirm={handleSubmitBulk}
      />
    </>
  )
}
